import React, { useRef } from "react";
import { debounce } from "lodash/fp";
import {
  Button,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";

import { useLazyQuery } from "@apollo/client/react";
import { isUndefined } from "lodash";
import { CompanyTypeEnum, ClientCreate, GetCompaniesHouseInfoDocument, CompaniesHouseInfo } from "@/graphql/generated/graphql";
import type { ClientUpdate } from "./UpdateClientModal";
import EssealPhoneInput from "../EssealPhoneInput";
import { titleize } from "inflection";

const normalDebounce = debounce(500);

const COMPANY_TYPE_OPTIONS = [
  { value: CompanyTypeEnum.Ltd, label: "Private Limited Company (LTD)" },
  { value: CompanyTypeEnum.Plc, label: "Public Limited Company (PLC)" },
  { value: CompanyTypeEnum.Llp, label: "Limited Liability Partnership (LLP)" },
  { value: CompanyTypeEnum.Lp, label: "Limited Partnership (LP)" },
  { value: CompanyTypeEnum.GeneralPartnership, label: "General Partnership" },
  { value: CompanyTypeEnum.SoleTrader, label: "Sole Trader" },
  { value: CompanyTypeEnum.Cic, label: "Community Interest Company (CIC)" },
  { value: CompanyTypeEnum.Cio, label: "Charitable Incorporated Organisation (CIO)" },
];

type AllowedClientTypes = ClientCreate | ClientUpdate;

interface ClientFieldsProps<T extends AllowedClientTypes> {
  clientName?: string;
  client: T;
  setClient: React.Dispatch<React.SetStateAction<T>>;
}

const ClientFields = <T extends AllowedClientTypes>({
  client,
  setClient,
  clientName
}: ClientFieldsProps<T>) => {
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;

  const [getCompanyInfo, { data, loading }] = useLazyQuery(GetCompaniesHouseInfoDocument, {
    fetchPolicy: "network-only",
  });

  // Use keyof ClientCreate to ensure type safety
  const handleOnChange = (key: keyof ClientCreate, value: string) => {
    setClient((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (key === "companyNumber" && value.trim()) {
      debounceFunc.cancel();
      debounceFunc(() => {
        getCompanyInfo({
          variables: { companyNumber: value },
        });
      });
    }
  };

  const handleAutofill = (info: CompaniesHouseInfo | undefined) => {
    if (isUndefined(info) || !info?.success || !info?.data) return;

    const { name, companyType, address } = info.data;

    setClient((prev) => ({
      ...prev,
      companyName: titleize(name),
      companyType: companyType,
      address: {
        street: address?.street ?? "",
        city: address?.city ?? "",
        state: address?.state ?? "",
        zip: address?.zip ?? "",
        country: "United Kingdom", // has to be hard coded for now
        locationType: "main"
      }
    }));
  };

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <TextField
          fullWidth
          label="Company Number"
          required
          value={client.companyNumber}
          error={!client.companyNumber?.trim()}
          onChange={(e) => handleOnChange("companyNumber", e.target.value)}
        />
        {
          !loading && client.companyNumber?.trim() && data?.getCompaniesHouseInfo &&
          <Button size="small" onClick={() => handleAutofill(data?.getCompaniesHouseInfo)} >
            Autofill Company Data
          </Button>
        }
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Company Name"
          autoFocus
          required
          error={!client.companyName?.trim()}
          value={client.companyName ?? clientName}
          onChange={(e) => handleOnChange("companyName", e.target.value)}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <Select
          fullWidth
          value={client.companyType || CompanyTypeEnum.Ltd}
          onChange={(e: SelectChangeEvent<CompanyTypeEnum>) =>
            handleOnChange("companyType", e.target.value as string)
          }
        >
          {COMPANY_TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="Trading Name"
          onChange={(e) => handleOnChange("tradingName", e.target.value)}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label="VAT Number"
          onChange={(e) => handleOnChange("vatNumber", e.target.value)}
        />
      </Grid>

      <Grid size={12}>
        <TextField
          fullWidth
          label="Email Address"
          onChange={(e) => handleOnChange("email", e.target.value)}
        />
      </Grid>

      <Grid size={12}>
        <EssealPhoneInput
          defaultCountry="GB"
          defaultValue={client.phone as string}
          onChange={(e164) => {
            handleOnChange("phone", e164)
          }}
        />
      </Grid>
    </Grid>
  );
};

export default ClientFields;
